import React, { useState, useEffect } from "react";
import { X } from 'lucide-react';
import "../../styles/ApproveRejectPopup.css"
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import SapCsvSafeInput from "../../CommonRemarksRestriction/SapCsvSafeInput";
import AsyncSelect from "react-select/async";
import axiosInstance from "../../axiosInstance";

const ApproveRejectPopup = ({
    isOpen,
    onClose,
    onSubmit,
    actionType,
    invoice,
    isLoading
}) => {
    const [comment, setComment] = useState("");
    const [error, setError] = useState("");
    const header = invoice?.approvalData?.invoice_header;
    const lines = invoice?.approvalData?.invoice_lines || [];
    const [lineData, setLineData] = useState([]);
    const backdatedDays = lines?.[0]?.backdated_days || 0;

    const getMinBillingDate = () => {
        const today = new Date();
        today.setDate(today.getDate() - backdatedDays);
        return today.toISOString().split("T")[0];
    };

    const [formData, setFormData] = useState({
        remarks: "",
        billing_nature: "",
        supply_period: null,
        billing_date: ""
    });

    useEffect(() => {
        if (isOpen && header) {
            setComment("");
            setError("");

            setFormData({
                remarks: header.sales_header_remarks || "",
                billing_nature: header.billing_nature || "",
                supply_period: header.supply_period || "",
                billing_date: formatDate(header.billing_date) || ""
            });
        }
    }, [isOpen, header]);

    useEffect(() => {
        if (lines.length > 0) {
            setLineData(
                lines.map(line => ({
                    ...line,
                    plant_id: line.plant_id || null,
                    plant_code: line.plant_code || "",
                    po_wo_number: line.po_wo_number || "",
                    po_wo_date: line.po_wo_date || ""
                }))
            );
        }
    }, [lines]);


    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const [day, month, year] = dateStr.split("-");
        return `${year}-${month}-${day}`;
    };

    const formatSupplyPeriod = (date) => {
        if (!date) return null;

        const options = { month: "short", year: "numeric" };
        return new Date(date).toLocaleDateString("en-US", options).replace(" ", "-");
    };

    useEffect(() => {
        if (isOpen) {
            setComment("");
            setError("");
        }
    }, [isOpen]);

    if (!isOpen || !invoice) return null;

    const handleSubmit = () => {
        if (isLoading) return;
        if (!comment.trim()) {
            setError("Comment is required.");
            toast.error(`${actionType === "approved" ? "Approve" : "Reject"} comments are mandatory`);
            return;
        }

        const payload = {
            comments: comment,
            billing_nature: formData.billing_nature,
            supply_period: formatSupplyPeriod(formData.supply_period),
            billing_date: formData.billing_date,
            customer_remarks: formData.remarks,
            plant_id: lineData[0]?.plant_id || null,
            plant_code: lineData[0]?.plant_code || "",
            po_wo_number: lineData[0]?.po_wo_number || "",
            po_wo_date: lineData[0]?.po_wo_date || ""
        };

        onSubmit(payload);
    };

    const handleLineChange = (field, value, index = 0) => {
        const updated = [...lineData];
        updated[index] = {
            ...updated[index],
            [field]: value
        };
        setLineData(updated);
    };

    const loadPlantCodeOptions = async (inputValue) => {
        try {

            const res = await axiosInstance.get("/plant_code/autosuggestion", {
                params: {
                    term: inputValue || "",
                    division_type: header?.division_type
                }
            });

            return res.data.autosuggestion.map(item => ({
                label: item.label,
                value: item.value
            }));

        } catch (error) {
            console.error("Plant code autosuggest error", error);
            return [];
        }
    };


    return (
        <div className="approve-reject-modal" onClick={onClose}>
            <div
                className="approve-reject-modal-content"
                onClick={(e) => e.stopPropagation()} >

                <div className="approve-reject-submit-header">
                    <h3>
                        {actionType === "approved" ? "Approve" : "Reject"} Invoice
                    </h3>

                    <div
                        className="approve-reject-close-btn" onClick={onClose}>
                        <X size={16} />
                    </div>
                </div>

                <div className="approve-reject-scroll-body">
                    <div className="approve-reject-form-group">
                        <label className="approve-reject-label">{actionType === 'approved' ? 'Approve' : 'Reject'} Comments <span style={{ color: 'red' }}>*</span></label>
                        <textarea
                            value={comment}
                            onChange={(e) => {
                                let value = e.target.value;
                                value = value.replace(/,/g, "");
                                value = value.replace(/^\s+/, "");
                                setComment(value);
                                if (value.trim()) setError("");
                            }}
                            placeholder="Enter your comments"
                        />
                    </div>

                    {error && <div className="error-msg">{error}</div>}

                    <div className="approve-reject-popup-container">
                        <div className="approve-reject-popup-grid">

                            {/* Remarks */}
                            <div className="approve-reject-popup-form-group approve-reject-remarks-span">
                                <label className="approve-reject-popup-label">
                                    Remarks to Customer
                                </label>
                                <SapCsvSafeInput
                                    className="approve-reject-popup-input"
                                    placeholder="Enter your Remarks..."
                                    value={formData.remarks}
                                    maxLength={255}
                                    style={{ margin: "0px" }}
                                    onChange={(val) =>
                                        setFormData({ ...formData, remarks: val })
                                    }
                                />
                                <div className="approve-reject-popup-helper">
                                    <span className="approve-reject-popup-error"></span>
                                    <span>{formData.remarks.length}/255</span>
                                </div>
                            </div>

                            {/* Billing Nature */}
                            <div className="approve-reject-popup-form-group">
                                <label className="approve-reject-popup-label">
                                    Billing Nature
                                </label>
                                <SapCsvSafeInput
                                    className="approve-reject-popup-input"
                                    placeholder="Enter Billing Nature"
                                    value={formData.billing_nature}
                                    maxLength={18}
                                    style={{ margin: "0px" }}
                                    onChange={(val) =>
                                        setFormData({ ...formData, billing_nature: val })
                                    }
                                />
                                <div className="approve-reject-popup-helper">
                                    <span className="approve-reject-popup-error"></span>
                                    <span>{formData.billing_nature.length}/18</span>
                                </div>
                            </div>

                            {/* Supply Period */}
                            <div className="approve-reject-popup-form-group">
                                <label className="approve-reject-popup-label">
                                    Supply Period
                                </label>
                                <DatePicker
                                    selected={formData.supply_period}
                                    onChange={(date) =>
                                        setFormData({ ...formData, supply_period: date })
                                    }
                                    dateFormat="MMM-yyyy"
                                    showMonthYearPicker
                                    className="approve-reject-popup-date"
                                    placeholderText="MMM-YYYY"
                                    style={{ margin: '0px' }}
                                    disabled
                                />
                            </div>

                            {/* Billing Date */}
                            <div className="approve-reject-popup-form-group">
                                <label className="approve-reject-popup-label">
                                    Billing Date
                                </label>
                                <input
                                    type="date"
                                    className="approve-reject-popup-input"
                                    value={formData.billing_date}
                                    min={getMinBillingDate()}
                                    max={new Date().toISOString().split("T")[0]}
                                    onChange={(e) =>
                                        setFormData({ ...formData, billing_date: e.target.value })
                                    }
                                />
                            </div>

                            {/* Plant Code */}
                            <div className="approve-reject-popup-form-group">
                                <label className="approve-reject-popup-label">
                                    Plant Code
                                </label>
                                <AsyncSelect
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={loadPlantCodeOptions}
                                    isClearable
                                    placeholder="Search Plant Code"
                                    value={
                                        lineData[0]?.plant_id
                                            ? {
                                                label: lineData[0].plant_code,
                                                value: lineData[0].plant_id
                                            }
                                            : null
                                    }
                                    onChange={(selected) => {
                                        const updated = [...lineData];
                                        updated[0] = {
                                            ...updated[0],
                                            plant_id: selected?.value || null,
                                            plant_code: selected?.label || ""
                                        };
                                        setLineData(updated);
                                    }}
                                    styles={{
                                        control: (base, state) => ({
                                            ...base,
                                            minHeight: "40px",
                                            borderRadius: "12px",
                                            fontSize: "13px",
                                            width: "100%",
                                            borderColor: state.isFocused ? "#454CF2" : "#D0D6DF",
                                            boxShadow: state.isFocused ? "0 0 0 1px #454CF2" : "none",
                                            "&:hover": { borderColor: "#454CF2" },
                                        }),
                                        container: (base) => ({ ...base, width: "100%" }),
                                        menu: (base) => ({ ...base, fontSize: "12px" }),
                                        menuList: (base) => ({
                                            ...base,
                                            maxHeight: "250px",
                                            overflowY: "auto"
                                        }),
                                    }}
                                />
                            </div>

                            {/* PO/WO NO*/}
                            <div className="approve-reject-popup-form-group">
                                <label className="approve-reject-popup-label">
                                    PO/WO Number
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter PO/WO Number"
                                    className="approve-reject-popup-input"
                                    value={lineData[0]?.po_wo_number || ""}
                                    onChange={(e) => handleLineChange("po_wo_number", e.target.value)}
                                    style={{ margin: '0px' }}
                                />
                            </div>

                            {/* PO/WO DATE*/}
                            <div className="approve-reject-popup-form-group">
                                <label className="approve-reject-popup-label">
                                    PO/WO Date
                                </label>
                                <input
                                    type="date"
                                    className="approve-reject-popup-input"
                                    value={lineData[0]?.po_wo_date || ""}
                                    onChange={(e) => handleLineChange("po_wo_date", e.target.value)}
                                />

                            </div>
                        </div>
                    </div>

                    {/* TABLE */}
                    <div className="invoice_popup_outercategory" style={{ backgroundColor: "#FFF7E8" }}>
                        <div className="invoice_popup_category_subheader">

                            <div className="invoice_popup_element_list">
                                <div style={{
                                    backgroundColor: "#19324C",
                                    color: "#fff",
                                    padding: "12px 16px",
                                    fontSize: "15px",
                                    fontWeight: 600,
                                    borderTopLeftRadius: "8px",
                                    borderTopRightRadius: "8px",
                                    borderRadius: '8px'
                                }}>
                                    INVOICE NO - {header?.inv_number || "-"}
                                </div>

                                <div className="table-container">
                                    <table className="invoice_table">
                                        <thead>
                                            <tr>
                                                <th style={{ backgroundColor: "#E6E9EE", textAlign: "center" }}>S.NO</th>
                                                <th style={{ backgroundColor: "#E6E9EE", textAlign: "center" }}>PROJECT CODE</th>
                                                <th style={{ backgroundColor: "#E6E9EE", textAlign: "center" }}>PLANT CODE</th>
                                                <th style={{ backgroundColor: "#E6E9EE", textAlign: "center" }}>CHARGE CODE</th>
                                                <th style={{ backgroundColor: "#E6E9EE", textAlign: "center" }}>DESCRIPTION</th>
                                                <th style={{ backgroundColor: "#E6E9EE", textAlign: "center" }}>PO/WO NO</th>
                                                <th style={{ backgroundColor: "#E6E9EE", textAlign: "center" }}>PO/WO DATE</th>
                                                <th style={{ backgroundColor: "#E6E9EE", textAlign: "center" }}>QTY</th>
                                                <th style={{ backgroundColor: "#E6E9EE", textAlign: "center" }}>RATE</th>
                                                <th style={{ backgroundColor: "#E6E9EE", textAlign: "center", padding: '10px' }}>NET TOTAL</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {lines.map((line, index) => (
                                                <tr key={index}>
                                                    <td>{index + 1}</td>
                                                    <td>{line.project_name}</td>
                                                    <td>{line.plant_code}</td>
                                                    <td>{line.charge_code || '-'}</td>
                                                    <td>{line.charge_name || '-'}</td>
                                                    <td>{line.po_wo_number || '-'}</td>
                                                    <td>{line.po_wo_date || '-'}</td>
                                                    <td>{line.unit_of_quantity}</td>
                                                    <td>{line.unit_of_rate}</td>
                                                    <td style={{ padding: '10px' }}>{line.net_total}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="approve-reject-modal-buttons">
                        <button onClick={onClose}>Cancel</button>
                        <button onClick={handleSubmit} disabled={isLoading}>{isLoading
                            ? actionType === "approved"
                                ? "Approving..."
                                : "Rejecting..."
                            : "Submit"}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApproveRejectPopup;
